/***********

MESH TEXTURE PAINTER by Thrax (C) - manthrax@gmail && vectorslave.com

***********/

import UnwrapUVs from "./UnwrapUVs.js"
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";

export default function PaintApp({ THREE, GLTFLoader, scene, camera, controls, renderer, gui, cursorNode, uPaintSourceTexture }) {

    let patch = (obj, fld, ck, fn) => obj[fld] = obj[fld].replace(`#include <${ck}>`, fn(THREE.ShaderChunk[ck], `#include <${ck}>`));

    function MeshPainter(sourceMesh) { }

    let simTime = 0;
    let fps = 60;
    let maxSubsteps = 10;

    let brushUniforms = {
        uBrushColor: { value: new THREE.Vector4(1, 0, 0, 1) },
        uBrushSize: { value: new THREE.Vector3(0.01, 0.01, 0.01) },
        uBrushHardness: { value: 0.5 },
        uBrushStrength: { value: 0.5 }
    };

    function ScenePainter(paintScene) {
        if (!paintScene) {
            console.error("ScenePainter: No paintScene / model passed! Cannot initialize.");
            return;
        }

        let paintMeshes = [];

        paintScene.traverse((e) => {
            if (e.isMesh && e.geometry && e.geometry.attributes && e.geometry.attributes.position) {
                paintMeshes.push(e);
            }
        });

        if (paintMeshes.length === 0) {
            console.error("ScenePainter: No valid mesh with position attribute found.");
            return;
        }

        let sourceMesh = paintMeshes[0];

        if (!sourceMesh.geometry.boundingBox) {
            try {
                sourceMesh.geometry.computeBoundingBox();
            } catch (e) {
                console.warn("Could not compute boundingBox, using default");
                sourceMesh.geometry.boundingBox = new THREE.Box3(
                    new THREE.Vector3(-1, -1, -1),
                    new THREE.Vector3(1, 1, 1)
                );
            }
        }

        if (!sourceMesh.geometry.boundingSphere) {
            try {
                sourceMesh.geometry.computeBoundingSphere();
                // Check if radius is NaN
                if (!sourceMesh.geometry.boundingSphere || 
                    isNaN(sourceMesh.geometry.boundingSphere.radius) ||
                    !isFinite(sourceMesh.geometry.boundingSphere.radius)) {
                    sourceMesh.geometry.boundingSphere = new THREE.Sphere(
                        new THREE.Vector3(0, 0, 0),
                        1
                    );
                }
            } catch (e) {
                console.warn("Could not compute boundingSphere, using default");
                sourceMesh.geometry.boundingSphere = new THREE.Sphere(
                    new THREE.Vector3(0, 0, 0),
                    1
                );
            }
        }

        if (!sourceMesh.geometry.boundingSphere) {
            sourceMesh.geometry.computeBoundingSphere();
        }

        let bounds = new THREE.Box3().setFromObject(sourceMesh);
        let size = bounds.getSize(new THREE.Vector3());
        let maxsz = Math.max(size.x, size.y, size.z);

        if (!isFinite(maxsz) || maxsz === 0) {
            console.error("ScenePainter: Invalid mesh size, cannot normalize.");
            return;
        }

        sourceMesh.scale.multiplyScalar(1 / maxsz);
        sourceMesh.updateMatrixWorld(true);

        bounds.setFromObject(sourceMesh);
        sourceMesh.position.sub(bounds.getCenter(new THREE.Vector3()));

        let paintMesh = sourceMesh.clone();

        if (!paintMesh.geometry || !paintMesh.geometry.attributes.position) {
            console.error("ScenePainter: Cloned mesh has invalid geometry.");
            return;
        }

        paintMesh.material = paintMesh.material.clone();

        let uCamViewMatrix = { value: camera.matrixWorldInverse };
        let uCamProjectionMatrix = { value: camera.projectionMatrix };
        let uBrushPoint = { value: new THREE.Vector3(0, -1000, 0) };
        let uBrushNormal = { value: new THREE.Vector3(0, 1, 0) };
        let uCursorPosition = { value: cursorNode?.fragMouse || new THREE.Vector2() };
        let uMatrixWorld = { value: paintMesh.matrixWorld };

        let setUniforms = (shader) => {
            shader.uniforms.uMatrixWorld = uMatrixWorld;
            shader.uniforms.uCamViewMatrix = uCamViewMatrix;
            shader.uniforms.uCamProjectionMatrix = uCamProjectionMatrix;
            shader.uniforms.uBrushNormal = uBrushNormal;
            shader.uniforms.uBrushPoint = uBrushPoint;
            shader.uniforms.uCursorPosition = uCursorPosition;
            shader.uniforms.uPaintSourceTexture = uPaintSourceTexture;
            Object.assign(shader.uniforms, brushUniforms);
        };

        paintMesh.material.onBeforeCompile = (shader) => {
            shader.vertexShader = `
uniform mat4 uMatrixWorld;
varying vec3 vWorldPosition;
` + shader.vertexShader;

            patch(shader, 'vertexShader', `fog_vertex`, (ck, ckinc) => {
                return ckinc + `
    vWorldPosition = (uMatrixWorld * vec4(transformed,1.0)).xyz;
    `;
            });

            shader.fragmentShader = `
uniform vec4 uBrushColor;
uniform vec3 uBrushPoint;
uniform vec3 uBrushSize;
uniform float uBrushHardness;
uniform float uBrushStrength;
uniform vec2 uCursorPosition;
varying vec3 vWorldPosition;

uniform mat4 uCamViewMatrix;
uniform mat4 uCamProjectionMatrix;

uniform sampler2D uPaintSourceTexture;
` + shader.fragmentShader;

            shader.fragmentShader = shader.fragmentShader.replace('}', `
gl_FragColor = gl_FragColor;
}`);

            setUniforms(shader);
        };

        let drawing = false;
        let buttons = 0;

        document.addEventListener('pointerdown', (e) => {
            if (e.target !== renderer.domElement) return;
            buttons = e.buttons;
            if (buttons === 1) {
                if (cursorNode?.raycast) {
                    controls.enabled = !(cursorNode.raycast(paintMesh).length > 0);
                }
                if (!controls.enabled) drawing = true;
            }
        });

        document.addEventListener('pointerup', () => {
            controls.enabled = true;
            drawing = false;
            buttons = 0;
        });

        let exportTriggered = false;

        this.exportScene = () => {
            const exporter = new GLTFExporter();
            exporter.parse(scene, (result) => {
                if (result instanceof ArrayBuffer) {
                    const blob = new Blob([result], { type: 'application/octet-stream' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = 'scene.glb';
                    link.click();
                }
            }, console.error, { binary: true });
        };

        this.update = (time) => {
            let substep = 0;
            while (simTime < time) {
                let dt = 1 / fps;
                simTime += dt;
                substep++;
                if (substep >= maxSubsteps) {
                    simTime = time;
                    break;
                }
            }
            if (exportTriggered) {
                exportTriggered = false;
                this.exportScene();
            }
        };

        if (gui) {
            gui.add({ export: () => { exportTriggered = true; } }, "export").name("export glb!");
            gui.add(brushUniforms.uBrushStrength, "value", 0, 1).name("Strength");
            gui.add(brushUniforms.uBrushHardness, "value", 0, 1).name("Hardness");
        }

        paintMesh.onBeforeRender = () => {
            let hits = cursorNode?.raycast ? cursorNode.raycast(paintMesh) : [];

            if (hits && hits[0] && hits[0].point && hits[0].face) {
                uBrushPoint.value.copy(hits[0].point);

                let normalMatrix = new THREE.Matrix3().getNormalMatrix(paintMesh.matrixWorld);
                let worldNormal = hits[0].face.normal.clone().applyMatrix3(normalMatrix).normalize();

                uBrushNormal.value.copy(worldNormal);
            } else {
                uBrushPoint.value.set(0, -1000, 0);
            }
        };

        scene.add(paintMesh);
    }

    this.render = (time) => {
        controls.update();
        renderer.setRenderTarget(null);
        renderer.render(scene, camera);
    };

    this.ScenePainter = ScenePainter;
}
